// Fatia S1: dados da empresa que deixam as estimativas e sugestões mais certeiras.
import { useState, type FormEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { CompanyDto, Goal } from '@impulso/shared'
import type { UpdateCompanyRequest } from '@impulso/shared/api/tenancy'
import { api, errorMessage } from '../../lib/api'
import { useCan, useCompany } from '../../lib/company'
import { useSession } from '../../lib/session'
import { useToast } from '../../ui/toast'
import { Empty, PageHead, Switch } from '../../ui'
import { FormError } from '../../features/auth/AuthShell'
import CompanySettingsTabs from '../../features/account/CompanySettingsTabs'
import { GOAL_OPTIONS, SEGMENTS, TIMEZONES, WEEKDAYS } from '../../features/onboarding/constants'

export default function SettingsBusiness() {
  const company = useCompany()
  const canEdit = useCan('company.edit_settings')
  const qc = useQueryClient()
  const { refresh } = useSession()
  const toast = useToast()
  const [name, setName] = useState(company.name)
  const [segment, setSegment] = useState(company.segment ?? '')
  const [city, setCity] = useState(company.city ?? '')
  const [goal, setGoal] = useState<Goal | ''>(company.goal ?? '')
  const [budget, setBudget] = useState(company.monthlyBudget?.toString() ?? '')
  const [ticket, setTicket] = useState(company.avgTicket?.toString() ?? '')
  const [closePer10, setClosePer10] = useState(company.conversionRate != null ? String(Math.round(company.conversionRate * 10)) : '')
  const [hasHours, setHasHours] = useState(!!company.businessHours)
  const [days, setDays] = useState<number[]>(company.businessHours?.days ?? [1, 2, 3, 4, 5, 6])
  const [open, setOpen] = useState(company.businessHours?.open ?? '08:00')
  const [close, setClose] = useState(company.businessHours?.close ?? '18:00')
  const [timezone, setTimezone] = useState(company.timezone)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  if (!canEdit) return <><PageHead title="Configurações" /><CompanySettingsTabs /><Empty title="Só gestores podem mudar os dados do negócio." /></>

  const num = (v: string) => (v.trim() === '' ? null : Number(v.replace(',', '.')))

  async function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    const conv = num(closePer10)
    if (conv != null && (conv < 0 || conv > 10)) return setError('Em “de cada 10 que chamam”, use um número de 0 a 10.')
    if (hasHours && !days.length) return setError('Escolha pelo menos um dia de atendimento.')
    const body: UpdateCompanyRequest = {
      name: name.trim(), segment: segment.trim() || null, city: city.trim() || null, goal: goal || null,
      monthlyBudget: num(budget), avgTicket: num(ticket), conversionRate: conv == null ? null : conv / 10,
      businessHours: hasHours ? { days: [...days].sort(), open, close } : null, timezone,
    }
    setBusy(true)
    try {
      const updated = await api.patch<CompanyDto>(`/companies/${company.id}`, body)
      qc.setQueryData(['company', company.id], updated)
      await refresh()
      toast('Informações salvas.')
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageHead title="Configurações" sub="Informações do seu negócio que deixam as estimativas e sugestões mais certeiras." />
      <CompanySettingsTabs />
      <form className="stack" style={{ gap: 20 }} onSubmit={submit}>
        <FormError error={error} />
        <div className="grid g-2">
          <div className="card stack" style={{ gap: 14 }}>
            <h2>Seu negócio</h2>
            <div className="field"><label htmlFor="sb-name">Nome</label><input id="sb-name" className="input" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} /></div>
            <div className="grid g-2" style={{ gap: 12 }}>
              <div className="field">
                <label htmlFor="sb-seg">Ramo</label>
                <input id="sb-seg" className="input" list="sb-segments" value={segment} onChange={(e) => setSegment(e.target.value)} />
                <datalist id="sb-segments">{SEGMENTS.filter((s) => s !== 'Outro').map((s) => <option key={s} value={s} />)}</datalist>
              </div>
              <div className="field"><label htmlFor="sb-city">Cidade</label><input id="sb-city" className="input" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Ex.: Recife, PE" /></div>
            </div>
            <div className="field">
              <label htmlFor="sb-goal">Objetivo principal</label>
              <select id="sb-goal" className="select" value={goal} onChange={(e) => setGoal(e.target.value as Goal | '')}>
                <option value="">Ainda não sei</option>
                {GOAL_OPTIONS.map((g) => <option key={g.id} value={g.id}>{g.title}</option>)}
              </select>
            </div>
            <div className="field">
              <label htmlFor="sb-tz">Fuso horário</label>
              <select id="sb-tz" className="select" value={timezone} onChange={(e) => setTimezone(e.target.value)}>
                {TIMEZONES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                {!TIMEZONES.some((t) => t.id === timezone) && <option value={timezone}>{timezone}</option>}
              </select>
            </div>
          </div>

          <div className="card stack" style={{ gap: 14 }}>
            <h2>Números do negócio</h2>
            <p className="small muted">Usados para calcular quanto custa cada cliente e o retorno estimado. Pode ser aproximado.</p>
            <div className="field"><label htmlFor="sb-budget">Teto de investimento por mês em anúncios (R$)</label><input id="sb-budget" className="input" inputMode="decimal" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="Ex.: 1500" /></div>
            <div className="grid g-2" style={{ gap: 12 }}>
              <div className="field"><label htmlFor="sb-ticket">Quanto cada cliente gasta, em média (R$)</label><input id="sb-ticket" className="input" inputMode="decimal" value={ticket} onChange={(e) => setTicket(e.target.value)} placeholder="Ex.: 85" /></div>
              <div className="field"><label htmlFor="sb-conv">De cada 10 que chamam, quantos compram</label><input id="sb-conv" className="input" inputMode="numeric" value={closePer10} onChange={(e) => setClosePer10(e.target.value)} placeholder="Ex.: 4" /></div>
            </div>
          </div>
        </div>

        <div className="card stack" style={{ gap: 14 }}>
          <div className="row between">
            <div><h2>Horário de atendimento</h2><p className="small muted">O piloto evita gastar quando ninguém pode responder.</p></div>
            <label className="row small" style={{ gap: 8 }}><Switch on={hasHours} label="Tenho horário fixo" onChange={setHasHours} /> Tenho horário fixo</label>
          </div>
          {hasHours && (
            <>
              <div className="row wrap" style={{ gap: 6 }} role="group" aria-label="Dias de atendimento">
                {WEEKDAYS.map((d, i) => (
                  <button type="button" key={d} aria-pressed={days.includes(i)} className={`chip ${days.includes(i) ? 'on' : ''}`} onClick={() => setDays(days.includes(i) ? days.filter((x) => x !== i) : [...days, i])}>{d}</button>
                ))}
              </div>
              <div className="row wrap" style={{ gap: 12 }}>
                <div className="field"><label htmlFor="sb-open">Abre às</label><input id="sb-open" className="input" type="time" value={open} onChange={(e) => setOpen(e.target.value)} /></div>
                <div className="field"><label htmlFor="sb-close">Fecha às</label><input id="sb-close" className="input" type="time" value={close} onChange={(e) => setClose(e.target.value)} /></div>
              </div>
            </>
          )}
        </div>
        <div className="form-actions">
          <button className="btn primary" disabled={busy}>{busy ? 'Salvando…' : 'Salvar'}</button>
        </div>
      </form>
    </>
  )
}
